import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LoggerService } from '../../../shared/services/logger.service';
import { ClaimsDataService } from '../services/claims-data.service';
import { PackageService } from '../services/package.service';
import { FarmFlexDeductibleClaim, FarmFlexDeductibleClaimsResult, FarmFlexDeductibleService } from './farm-flex-deductible.service';

interface FarmFlexDeductibleSummary {
    deductibleBalance: number | null;
    deductiblePaid: number | null;
    deductibleThreshold: number | null;
    deductibleTotal: number | null;
    maintenanceDeductible: number | null;
}

interface FarmFlexDeductibleTerm {
    fullPakNumber: string;
    id: string;
    label: string;
}

interface PakDataResponseItem {
    effectiveDate?: string;
    expirationDate?: string;
    policyNumber?: string;
    prefixA?: string;
    prefixB?: string;
}

@Component({
    selector: 'is-farm-flex-deductible',
    templateUrl: './farm-flex-deductible.component.html',
    styleUrls: ['./farm-flex-deductible.component.scss'],
    standalone: false
})
export class FarmFlexDeductibleComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren('pageNavigator') pageNavigators!: QueryList<ElementRef>;
    accountNumber = '';
    accountSummaryUrl = environment.NATIONWIDE.accountSummaryPage;
    claims: FarmFlexDeductibleClaim[] = [];
    currentPage = 1;
    deductibleSummary: FarmFlexDeductibleSummary | null = null;
    displayLimit = 10;
    displayOffset = 0;
    hasPackageError = false;
    insuredName = '';
    isLoadComplete = false;
    isLocalPreview = true;
    policyTerms: FarmFlexDeductibleTerm[] = [];
    selectedTermId = '';
    totalClaims = 0;
    userId = '';
    private attachedNavigator: HTMLElement | null = null;
    private boltEventName = 'bolt-pagination-change';
    private claimsRequest$ = new Subject<{ policyNumber: string; offset: number; limit: number; termId: string }>();
    private destroy$ = new Subject<void>();
    private packageSubscription?: Subscription;

    constructor(
        private claimsDataService: ClaimsDataService,
        private farmFlexDeductibleService: FarmFlexDeductibleService,
        private logger: LoggerService,
        private packageService: PackageService
    ) { }

    get placeholderTermId(): string {
        return this.policyTerms[0]?.id ?? '';
    }

    get placeholderTermLabel(): string {
        return this.policyTerms[0]?.label ?? '';
    }

    get policyNumber(): string {
        return this.selectedTerm?.fullPakNumber ?? '';
    }

    get selectablePolicyTerms(): FarmFlexDeductibleTerm[] {
        return this.policyTerms.slice(1);
    }

    get selectedTerm(): FarmFlexDeductibleTerm | undefined {
        return this.policyTerms.find((term) => term.id === this.selectedTermId);
    }

    get selectedTermClaims(): FarmFlexDeductibleClaim[] {
        return this.claims ?? [];
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.totalClaims / this.displayLimit));
    }

    goToPage(page: number): void {
        if (page < 1 || page > this.totalPages || page === this.currentPage) {
            return;
        }
        this.currentPage = page;
        this.displayOffset = (page - 1) * this.displayLimit;
        this.loadClaims(this.selectedTermId);
    }

    navigateBack(): void {
        window.location.href = this.accountSummaryUrl;
    }

    ngAfterViewInit(): void {
        this.bindNavigatorListener(this.pageNavigators.first);
        this.pageNavigators.changes.subscribe((list: QueryList<ElementRef>) => this.bindNavigatorListener(list.first));
    }

    ngOnDestroy(): void {
        this.detachNavigatorListener();
        this.destroy$.next();
        this.destroy$.complete();
        this.claimsRequest$.complete();
        this.packageSubscription?.unsubscribe();
    }

    ngOnInit(): void {
        this.logger.setComponentName('FarmFlexDeductibleComponent');
        this.logger.setPageName('FarmFlexDeductible');
        this.accountNumber = sessionStorage.getItem('policyNumber') ?? '';

        this.userId = sessionStorage.getItem('userId') ?? '';
        this.logger.info(`FarmFlexDeductibleComponent: Initializing for account ${this.accountNumber}, userId: ${this.userId}, policyNumber: ${this.policyNumber || 'N/A'}`);
        this.claimsRequest$
            .pipe(
                switchMap((request) =>
                    this.farmFlexDeductibleService
                        .searchClaimsByPolicyNumber(request.policyNumber, request.offset, request.limit)
                        .pipe(
                            map((result: FarmFlexDeductibleClaimsResult) => ({ result, termId: request.termId }))
                        )
                ),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: ({ result, termId }) => this.handleClaimsResponse(termId, result),
                error: (error: unknown) => this.handleClaimsError(error)
            });

        this.getPakData(this.accountNumber);
    }

    onTermChange(event: Event): void {
        const termSelect = <HTMLSelectElement>event.target;
        this.selectedTermId = termSelect?.value ?? '';
        this.currentPage = 1;
        this.displayOffset = 0;
        this.loadClaims(this.selectedTermId);
    }

    private bindNavigatorListener(reference: ElementRef | undefined): void {
        const element = <HTMLElement | undefined>reference?.nativeElement;
        if (this.attachedNavigator === element) {
            return;
        }
        this.detachNavigatorListener();
        if (element) {
            element.addEventListener(this.boltEventName, this.boltChangeListener);
            this.attachedNavigator = element;
        }
    }

    private boltChangeListener = (event: Event): void => {
        const detail = (<CustomEvent>event)?.detail;
        if (detail?.type !== 'page') {
            return;
        }
        const detailPage = Number(detail?.value ?? 0);
        if (detailPage > 0) {
            this.goToPage(detailPage);
        }
    };

    private detachNavigatorListener(): void {
        if (this.attachedNavigator) {
            this.attachedNavigator.removeEventListener(this.boltEventName, this.boltChangeListener);
            this.attachedNavigator = null;
        }
    }

    // for future use
    private formatCurrency(value: number | null): string {
        if (value === null || value === undefined) {
            return 'Not available';
        }

        const currencyFractionDigits = 2;
        return value.toLocaleString('en-US', {
            currency: 'USD',
            minimumFractionDigits: currencyFractionDigits,
            style: 'currency'
        });
    }

    private formatDateString(dateString: string): string {
        if (!dateString) {
            return '';
        }
        const [year, month, day] = dateString.split('-');
        return `${month}/${day}/${year}`;
    }

    private getPakData(accountNumber: string): void {
        this.packageSubscription = this.packageService.retrievePackageDataForFarmFlex(accountNumber).subscribe({
            next: (response) => {
                this.insuredName = this.claimsDataService.getClaimsPackage()?.policyAccount?.namedInsuredName ?? '';
                this.processPakDataResponse(response);
            },
            error: (error: unknown) => {
                this.logger.error(`FarmFlexDeductibleComponent: Failed to load package data - userId: ${this.userId}, policyNumber: ${this.policyNumber || 'N/A'}, accountNumber: ${this.accountNumber}`, <object>error);
                this.resetPolicyTerms();
                this.hasPackageError = true;
                this.isLoadComplete = true;
            }
        });
    }

    private handleClaimsError(error: unknown): void {
        this.logger.error(`FarmFlexDeductibleComponent: Failed to load claims - userId: ${this.userId}, policyNumber: ${this.policyNumber}`, <object>error);
        this.hasPackageError = true;
        this.isLoadComplete = true;
    }

    private handleClaimsResponse(termId: string, result: FarmFlexDeductibleClaimsResult): void {
        const { claims, deductiblePaid, deductibleTotal, maintenanceDeductible, deductibleThreshold, totalCount } = result;
        this.claims = claims;
        this.totalClaims = totalCount;
        this.deductibleSummary = {
            deductibleBalance: deductibleTotal !== null && deductiblePaid !== null ? deductibleTotal - deductiblePaid : null,
            deductiblePaid: deductiblePaid ?? null,
            deductibleThreshold: deductibleThreshold ?? null,
            deductibleTotal: deductibleTotal ?? null,
            maintenanceDeductible: maintenanceDeductible ?? null
        };
        this.isLoadComplete = true;
        this.logger.info(`FarmFlexDeductibleComponent: Page loaded successfully - userId: ${this.userId}, policyNumber: ${this.policyNumber}`);
        this.logger.debug(`FarmFlexDeductibleComponent: Loaded ${claims.length} claims (total=${totalCount}) for term "${termId}" - userId: ${this.userId}, policyNumber: ${this.policyNumber || 'N/A'}`);
    }

    private handleEmptyPakResponse(): void {
        this.resetPolicyTerms();
        this.hasPackageError = true;
        this.logger.error(`FarmFlexDeductibleComponent: No accounts loaded for the Package (PAK) on policy Claims list page - userId: ${this.userId}, policyNumber: ${this.policyNumber || 'N/A'}, accountNumber: ${this.accountNumber}`);
        this.isLoadComplete = true;
    }

    private initializePolicyTerms(response: PakDataResponseItem[]): void {
        this.policyTerms = this.mapResponseTerms(response);
        this.selectedTermId = this.policyTerms[0]?.id ?? '';
        this.currentPage = 1;
        this.displayOffset = 0;
        if (this.selectedTermId) {
            this.loadClaims(this.selectedTermId);
            return;
        }
        this.isLoadComplete = true;
    }

    private isEmptyPakResponse(response: PakDataResponseItem[] | null | undefined): boolean {
        return !response || response.length === 0;
    }

    private loadClaims(termId: string): void {
        this.claimsRequest$.next({
            policyNumber: this.policyNumber,
            offset: this.displayOffset,
            limit: this.displayLimit,
            termId
        });
    }

    private mapResponseTerms(response: PakDataResponseItem[]): FarmFlexDeductibleTerm[] {
        return [...response]
            .sort((firstTerm, secondTerm) => (secondTerm.effectiveDate ?? '').localeCompare(firstTerm.effectiveDate ?? ''))
            .map((pak) => {
                const formattedEffectiveDate = this.formatDateString(pak.effectiveDate ?? '');
                const formattedExpirationDate = this.formatDateString(pak.expirationDate ?? '');
                const termLabel = `${formattedEffectiveDate} - ${formattedExpirationDate}`;
                const fullPakNumber = `${pak.prefixA ?? ''} ${pak.prefixB ?? ''}${pak.policyNumber ?? ''}`.trim();
                return {
                    fullPakNumber,
                    id: fullPakNumber,
                    label: termLabel
                };
            });
    }

    private processPakDataResponse(response: PakDataResponseItem[] | null | undefined): void {
        try {
            if (this.isEmptyPakResponse(response)) {
                this.handleEmptyPakResponse();
                return;
            }

            this.initializePolicyTerms(<PakDataResponseItem[]>response);
        } catch (error) {
            this.resetPolicyTerms();
            this.hasPackageError = true;
            this.logger.error(`FarmFlexDeductibleComponent: Error processing package data - userId: ${this.userId}, policyNumber: ${this.policyNumber || 'N/A'}, accountNumber: ${this.accountNumber}`, <object>error);
            this.isLoadComplete = true;
        }
    }

    private resetPolicyTerms(): void {
        this.policyTerms = [];
        this.selectedTermId = '';
        this.claims = [];
        this.totalClaims = 0;
    }
}
